import { getAxiosClient } from '~src/api';
import Swal from 'sweetalert2';
import { changeImageSrc } from '~src/utils';
import { onErrorShowMessage } from '~src/errors';
import { getState } from '~src/state';
import { triggerStateChangedRequest } from '~core';
import { trackEvent } from '~src/tracking';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { prepareImageForEditing } from '~core/image';
import { getBase64ImageByUrl } from '~src/utils';
import { b64toBlob } from '~core';
import {
  getAllFeatures,
  DEFAULT_AI_LOADING_MESSAGES,
} from '~core/aiEdit/aiFeatureCategories';
import { isPlaceholderEditing } from '~core/magicFrame';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';
import {
  removeColorFromImageData,
  hexToRgb,
} from '~core/magicWand/colorRemoval';
import { postEncryptedGiottoApiPayload } from '~core/aiBackground/promptEncryption';
import {
  chooseObjectRemovalMode,
  OBJECT_REMOVAL_PROMPTS,
} from './objectRemovalModeChoice';
import {
  AI_EDIT_RESULT_ERRORS,
  getAiEditResultError,
  showModeratedResultMessage,
} from './aiEditResult';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const applyDissolvenzaEffect = async (imageBase64) => {
  // Create a temporary image element to process with Magic Wand
  const img = new Image();
  img.src = `data:image/png;base64,${imageBase64}`;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  // Create a canvas to get ImageData
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = img.width;
  tempCanvas.height = img.height;
  const ctx = tempCanvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  // Get ImageData and apply Magic Wand
  const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);

  // Convert white color to RGB
  const targetColor = hexToRgb('#ffffff');

  // Apply Magic Wand with the same default settings as the UI
  const processedImageData = removeColorFromImageData(
    imageData,
    targetColor, // Remove white
    80, // Default tolerance
    'feather', // Feather smoothing (default from UI)
    5, // Default smooth radius
    true, // Connected only (default from UI)
    null // No specific start point (will use edges)
  );

  // Put processed data back on canvas
  ctx.putImageData(processedImageData, 0, 0);

  // Convert back to base64 - using PNG to preserve transparency
  const processedDataUrl = tempCanvas.toDataURL('image/png');
  return processedDataUrl.split(',')[1];
};

const startLoader = (
  loadingMessages = DEFAULT_AI_LOADING_MESSAGES,
  title = '✨ Elaborazione in corso',
  totalDurationMs = 16800
) => {
  let currentMessageIndex = 0;
  let messageInterval;
  const progressDurationMs =
    Number.isFinite(totalDurationMs) && totalDurationMs > 0
      ? totalDurationMs
      : 16800;

  const swalInstance = Swal.fire({
    title,
    html: `
      <div class="ai-loader-container" style="--progress-duration: ${progressDurationMs}ms;">
        <div class="loader-visual">
          <div class="loader-circle">
            <div class="loader-progress"></div>
            <div class="loader-icon">${loadingMessages[0].icon}</div>
          </div>
        </div>
        
        <div class="status-message">
          <span class="status-icon">${loadingMessages[0].icon}</span>
          <span class="status-text">${loadingMessages[0].text}</span>
        </div>
        
        <div class="progress-bar-container">
          <div class="progress-bar-fill"></div>
        </div>
        
        <div class="warning-text">
          Non chiudere questa pagina durante l'elaborazione 🙏
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    showCancelButton: false,
    customClass: {
      popup: 'animated fadeIn',
    },
    didOpen: () => {
      const container = Swal.getHtmlContainer();
      if (!container) return;

      const statusIcon = container.querySelector('.status-icon');
      const statusText = container.querySelector('.status-text');
      const loaderIcon = container.querySelector('.loader-icon');

      if (!statusIcon || !statusText || !loaderIcon) {
        console.error('Could not find loader elements');
        return;
      }

      const updateMessage = (message) => {
        // Reset animations
        statusIcon.style.animation = '';
        statusText.style.animation = '';

        // Force reflow to restart animation
        void statusIcon.offsetWidth;
        void statusText.offsetWidth;

        // Update content and restart animations
        statusIcon.textContent = message.icon;
        statusText.textContent = message.text;
        statusIcon.style.animation = 'iconPop 0.3s ease-out';
        statusText.style.animation = 'fadeIn 0.3s ease-out';

        // Update central icon in sync with status icon
        loaderIcon.textContent = message.icon;
        loaderIcon.style.animation = '';
        void loaderIcon.offsetWidth; // Force reflow
        loaderIcon.style.animation = 'loaderPulse 2s ease-in-out infinite';
      };

      const intervalDuration = progressDurationMs / loadingMessages.length;

      messageInterval = setInterval(() => {
        currentMessageIndex++;
        if (currentMessageIndex < loadingMessages.length) {
          updateMessage(loadingMessages[currentMessageIndex]);
        }
      }, intervalDuration);

      // Store the update function and interval reference on the swal instance
      swalInstance.updateMessage = updateMessage;
      swalInstance.messageInterval = messageInterval;
    },
    willClose: () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    },
  });

  return swalInstance;
};

const stopLoader = () => {
  Swal.hideLoading();
  Swal.close();
};

export default async function handleAiEdit(prompt) {
  const allowUseAiEdit = await isFeatureEnabled(FEATURES.AI_USE_AI_EDIT, false);
  if (!allowUseAiEdit) {
    alert('Questa funzione non è disponibile nel tuo piano di abbonamento');
    return;
  }

  const requestedPrompt = prompt;

  // Find the feature configuration based on the prompt
  const allFeatures = getAllFeatures();
  // Handle both regular prompts and dissolvenza with background prompts
  const basePrompt = requestedPrompt.startsWith('DISSOLVENZA_')
    ? 'DISSOLVENZA'
    : requestedPrompt;
  const feature = allFeatures.find((f) => f.prompt === basePrompt);
  const featureName = feature?.title || 'AI Edit';

  let objectRemovalMode = null;
  if (requestedPrompt === OBJECT_REMOVAL_PROMPTS.FAST) {
    objectRemovalMode = await chooseObjectRemovalMode();
    if (!objectRemovalMode) {
      return;
    }
    prompt = objectRemovalMode.prompt;
  }

  // Check credits before starting the operation
  const hasCredits = await checkAiCreditsBeforeUse({
    featureName,
    requiredCredits: 1,
  });

  if (!hasCredits) {
    return;
  }

  const startTime = performance.now();
  let selection = window.canvas.getActiveObject();
  if (selection.type === 'MagicFrame') {
    selection = selection.editingImg;
  }

  if (isPlaceholderEditing(selection)) {
    return;
  }

  const imageUrl = await prepareImageForEditing(selection, false);
  if (!imageUrl) {
    console.error('No image URL found in current selection', selection);
    return;
  }

  const loadingMessages =
    feature?.loadingMessages || DEFAULT_AI_LOADING_MESSAGES;
  const title = feature?.title
    ? `✨ ${feature.title}`
    : '✨ Elaborazione in corso';

  const loaderInstance = startLoader(
    loadingMessages,
    title,
    objectRemovalMode?.loaderDurationMs || feature?.loaderDurationMs
  );

  const payload = {
    image_url: imageUrl,
    prompt: prompt,
    output_format: 'png',
  };

  let queueResponse;
  let response;

  const maxAttempts = 100;
  const httpClient = await getAxiosClient();
  const httpClientWithRetries = await getAxiosClient(true, maxAttempts, {
    retryDelayMs: 1000,
  });

  try {
    queueResponse = await postEncryptedGiottoApiPayload(
      httpClient,
      `${process.env.GIOTTO_API_URL}/ai-edit`,
      payload
    );
  } catch (err) {
    console.error(err);
    trackEvent('AI Edit', {
      success: false,
      prompt: requestedPrompt,
      object_removal_mode: objectRemovalMode?.key,
      time_taken: parseInt(performance.now() - startTime, 10),
    });
    stopLoader();
    return onErrorShowMessage();
  }

  await sleep(1000);

  try {
    let statusRouteParam = '';
    if (queueResponse.data.featureKey) {
      statusRouteParam = `&featureKey=${encodeURIComponent(
        queueResponse.data.featureKey
      )}`;
    } else if (queueResponse.data.model) {
      statusRouteParam = `&model=${encodeURIComponent(
        queueResponse.data.model
      )}`;
    }
    response = await httpClientWithRetries.get(
      `${process.env.GIOTTO_API_URL}/ai-edit?requestId=${queueResponse.data.requestId}${statusRouteParam}`
    );
  } catch (err) {
    console.error(err);
    trackEvent('AI Edit', {
      success: false,
      prompt: requestedPrompt,
      object_removal_mode: objectRemovalMode?.key,
      time_taken: parseInt(performance.now() - startTime, 10),
    });
    stopLoader();
    return onErrorShowMessage();
  }

  // Stop before saving: an attachment would replace the photo on the canvas
  // and consume an AI credit (PE-842).
  const resultError = getAiEditResultError(response.data);
  if (resultError) {
    trackEvent('AI Edit', {
      success: false,
      prompt: requestedPrompt,
      object_removal_mode: objectRemovalMode?.key,
      time_taken: parseInt(performance.now() - startTime, 10),
      error: resultError,
    });
    stopLoader();
    if (resultError === AI_EDIT_RESULT_ERRORS.CONTENT_MODERATED) {
      return showModeratedResultMessage(featureName);
    }
    return onErrorShowMessage();
  }

  // Stop automatic message rotation and show saving step
  if (loaderInstance.messageInterval) {
    clearInterval(loaderInstance.messageInterval);
  }
  if (loaderInstance.updateMessage) {
    loaderInstance.updateMessage({
      icon: '💾',
      text: 'Salvando la foto tra gli allegati...',
    });

    // Also update the progress bar to show near completion
    const progressBar = document.querySelector('.progress-bar-fill');
    if (progressBar) {
      progressBar.style.animation = 'none';
      progressBar.style.width = '90%';
      progressBar.style.transition = 'width 0.5s ease-out';
    }
  }

  try {
    let imageBase64 = await getBase64ImageByUrl(response.data.imageUrl);

    // Special handling for DISSOLVENZA: apply Magic Wand to remove white background
    if (prompt === 'DISSOLVENZA' || prompt.startsWith('DISSOLVENZA_')) {
      imageBase64 = await applyDissolvenzaEffect(imageBase64);
    }

    const blob = b64toBlob(
      imageBase64,
      prompt === 'DISSOLVENZA' || prompt.startsWith('DISSOLVENZA_')
        ? 'image/png'
        : 'image/jpeg'
    );
    const attachmentsUploader = getState('uploader.allegati');

    attachmentsUploader.onComplete = attachmentsUploader.addFile({
      type: blob.type,
      data: blob,
      meta: {
        customAttachmentProps: {
          aiEditRawImage: imageUrl,
          isAiEdit: true,
          aiEditPrompt: prompt,
        },
      },
    });

    const responseUpload = await attachmentsUploader.upload();

    if (!responseUpload.successful || responseUpload.successful.length === 0) {
      throw new Error('Upload to attachments failed');
    }

    attachmentsUploader.cancelAll();

    const newImageUrl = `${process.env.EDGE_RESIZER}${
      new URL(responseUpload.successful[0].uploadURL).pathname
    }`;

    let magicFrame = selection._magicFrame;
    if (selection.type === 'MagicFrame') {
      magicFrame = selection;
    }

    if (magicFrame) {
      await magicFrame.updatePerson(newImageUrl);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
      magicFrame.scalePersonToWidth(magicFrame.width * magicFrame.scaleX);
    } else {
      const currentProps = {
        scaleX: selection.scaleX,
        scaleY: selection.scaleY,
        top: selection.top,
        left: selection.left,
      };

      await changeImageSrc(selection, newImageUrl);

      selection.set({
        ...currentProps,
      });
    }
    window.canvas.renderAll();

    triggerStateChangedRequest();

    stopLoader();

    trackEvent('AI Edit', {
      success: true,
      prompt: requestedPrompt,
      object_removal_mode: objectRemovalMode?.key,
      time_taken: parseInt(performance.now() - startTime, 10),
    });

    // Show success message with enhanced UI
    const successTitle = feature?.title || 'Modifica completata';
    Swal.fire({
      title: `✨ ${successTitle}!`,
      html: `
        <div class="success-container">
          <div class="success-icon-wrapper">
            <div class="sparkle-container">
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
              <div class="success-sparkle"></div>
            </div>
            <div class="success-circle">
              <div class="success-icon">✅</div>
            </div>
          </div>
          <div class="success-message">La foto è stata modificata con successo!</div>
        </div>
      `,
      timer: 3000,
      showConfirmButton: false,
      customClass: {
        popup: 'animated fadeIn',
      },
    });
  } catch (error) {
    console.error('Error saving to attachments:', error);
    stopLoader();

    // Clean up uploader if it exists
    const attachmentsUploader = getState('uploader.allegati');
    if (attachmentsUploader) {
      attachmentsUploader.cancelAll();
    }

    trackEvent('AI Edit', {
      success: false,
      prompt: requestedPrompt,
      object_removal_mode: objectRemovalMode?.key,
      time_taken: parseInt(performance.now() - startTime, 10),
      error: 'attachment_upload_failed',
    });
    return onErrorShowMessage();
  }
}
